import { type ComponentType, lazy } from 'react';

/**
 * `React.lazy` wrapper that survives stale chunks.
 *
 * When Vite re-optimizes dependencies in dev, or a production deploy
 * publishes new chunk hashes while a tab is still open, the URLs the
 * browser cached for previous dynamic imports stop existing. The next
 * `import('./page')` then rejects with `Failed to fetch dynamically
 * imported module`, which surfaces as a hard crash inside React.
 *
 * This helper:
 *   1. Retries the import once after a small delay (handles transient
 *      network blips).
 *   2. If the second attempt also fails with a chunk-load style error,
 *      forces a single full-page reload — the new HTML will reference
 *      the current chunk URLs.
 *   3. Uses `sessionStorage` to make sure we never reload more than
 *      once per page-load to avoid an infinite loop if the failure is
 *      genuine (e.g. the server is down).
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  // Stable key per import site; if omitted we derive one from the factory
  // source so multiple lazy() calls don't share the same reload cooldown.
  key?: string,
): ReturnType<typeof lazy<T>> {
  const reloadKey = `lazyWithRetry:reloaded:${key ?? factory.toString().slice(0, 64)}`;

  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;

      try {
        await delay(200);
        return await factory();
      } catch (retryErr) {
        if (!isChunkLoadError(retryErr)) throw retryErr;

        // Only auto-reload once per session — if the new HTML still can't
        // load this chunk, surface the boundary instead of looping.
        if (typeof window !== 'undefined' && !window.sessionStorage.getItem(reloadKey)) {
          try {
            window.sessionStorage.setItem(reloadKey, '1');
          } catch {
            // Storage might be disabled; reloading once is still safer
            // than throwing into the user's face.
          }
          window.location.reload();
          // Return a never-resolving promise so React keeps the suspense
          // fallback up while the page reloads.
          return new Promise<{ default: T }>(() => undefined);
        }
        throw retryErr;
      }
    }
  });
}

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  let message: string;
  let name = '';
  if (err instanceof Error) {
    message = err.message;
    name = err.name;
  } else if (typeof err === 'string') {
    message = err;
  } else {
    return false;
  }
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
